import React, {useEffect, useState} from 'react';
import { gql } from 'apollo-boost';
import { useMutation } from '@apollo/react-hooks';
import Button, {LoadingButton} from '../../Form/Button/Button';
import ButtonGroup from '../../Form/Button/ButtonGroup';
import Modal from '../../Modal/Modal';
import SmsOtp from '../../Session/SmsOtp';
import Select from '../../Form/Select/Select';
import TextAreaInput from '../../Form/TextInput/TextAreaInput';
import closureReasons from './closureReasons';
import withFeatureFlags from '../../FeatureFlags/withFeatureFlags';

import './accountClosure.scss';
import successImg from "../../../assets/images/success.png";

/** @import {ValueType} from 'react-select' */
/** @import {OptionType} from '../../Form/Select/SelectTypes' */
/** @import {WithFeatureFlagsProps} from '../../FeatureFlags/FeatureFlagsTypes' */
/** @import {AccountClosureProps, StepFourProps, StepOneProps, StepThreeProps, StepTwoProps} from './AccountClosureTypes' */
/** @import {VerifyOtpOnSuccess} from '../../Session/VerifyOtpTypes' */

const CLOSE_ACCOUNT = gql`
  mutation CloseAccount($request : CloseAccountRequestType!){
    closeAccount(request:$request) {
      success
    }
  }   
`;

const shouldDisableButton = (/** @type {string} */ status) => {
  if (status === "ClosureRequested" || status === "Closed")
    return true;
  
  return false;
};

const AccountClosure = ( /** @type {AccountClosureProps & WithFeatureFlagsProps} */ props) => {
  const [modalVisible, setModalVisisble] = useState(false);
  const [step, setStep] = useState(1);
  const [completionToken, setCompletionToken] = useState(/** @type {string | undefined} */ (''));
  const [closeAccount] = useMutation(CLOSE_ACCOUNT);
  const [disable, setDisable] = useState(shouldDisableButton(props.accountStatus));

  useEffect(() => {
    if (modalVisible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [modalVisible]);
  
  const onCancel = () => {
    setStep(1);
    setModalVisisble(false);
  }

  const onCloseSuccess = () => {
    onCancel();
    if (props.onActionComplete)
        props.onActionComplete();
  }

    /** @type {VerifyOtpOnSuccess} */
    const onOtpSuccess = (_e, _m, completionToken) => {
    setCompletionToken(completionToken);
    setStep(3);
  };

  const onReasonEntered = async (/** @type {string} */ reason, /** @type {string} */ freeTextReason) => {
    const result = await closeAccount({
      variables: {
        request: {
          accountId: props.accountId,
          completionToken: completionToken,
          closureReason: reason,
          additionalClosureReason: freeTextReason
        }
      }
    });

    return {
      success: result.data.closeAccount.success,
      callback: () => {
        setStep(4);
        setDisable(true);
      }
    }
  };

  const renderStepNs = () => {
    if (step === 1)
      return <StepOne productName={props.productName} cancel={onCancel} onProceed={() => setStep(2)} />
    if (step === 2)
      return <StepTwo mobileNumber={props.mobileNumber} onProceed={onOtpSuccess} />
    if (step === 3)
      return <StepThree cancel={onCancel} onProceed={onReasonEntered} />
    if (step === 4)
      return <StepFour close={onCloseSuccess} />

    return <div></div>;
  };
  
  return (
    <>
      <Button size='med' label={`Close my ${props.productName}`} onClick={() => setModalVisisble(true)} disabled={disable} />
      {modalVisible && 
        (<Modal className="modal modal--fixed-height modal--wide inactivity-modal modal__mobile-optimised" onClose={step === 4 ? onCloseSuccess : onCancel} mobileOptimised>
          {renderStepNs()}
        </Modal>)
      }
    </>
  );
};

function StepOne(/** @type {StepOneProps} */ props) {
  return (
      <>
        <h2 className="modal__title">Closing your {props.productName}</h2>
        <p className="modal__slim">
          We're sorry you'd like to close your account.
        </p>
        <p className="modal__slim">
          You'll still have access to your online dashboard, even if you have no open accounts. From here you’ll be able to download your annual statements and interest certificates. We’ll let you know when these are available.
        </p>
        <p className="modal__slim">
          To continue, select 'Close my account' below and we'll text you a one-time password to confirm it's really you.
        </p>

        <div className="modal__action-group">
          <ButtonGroup>
            <Button size='med' 
              //@ts-expect-error className is not a valid prop
              className="account-closure__button" 
              label='Close my account' onClick={props.onProceed} />
            <Button variant='secondary' size='med' 
              //@ts-expect-error className is not a valid prop
              className="account-closure__button" 
              label="Don't close my account" secondary onClick={props.cancel} />
          </ButtonGroup>
        </div>
      </>
  );
};

function StepTwo(/** @type {StepTwoProps} */ props) {
  return (
    <>
    <h2 className="modal__title">Verify account closure</h2>
    <SmsOtp 
      mobileNumber={props.mobileNumber} 
      onSuccess={props.onProceed}
      analyticsRegion={"account details"}
      analyticsAction={"account closure"}
    />
    </>
  );
};

function StepThree(/** @type {StepThreeProps} */ props) {
  const [reason, setReason] = useState(/** @type {null | { value: ValueType<OptionType, false> | string; }} */ (null));
  const [freeTextReason, setFreeTextReason] = useState('');
  const [showSelectValidation, setShowSelectValidation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  const freeTextLimit = 250;
  const validate = () => {
    let valid = true;
    if (!reason) {
      valid = false;
      setShowSelectValidation(true);
    }

    // Free text is optional for both "unhappy_with_service" and "other"
    // No validation needed for optional fields

    return valid;
  };

  const proceed = async () => {
    setShowError(false);
    if (!validate()) {
      return;
    }

    setIsLoading(true);
    var result = await props.onProceed(
        //@ts-expect-error need to understand this better
        reason.value, 
        freeTextReason
    );
    setIsLoading(false);

    if (!result?.success) {
      setShowError(true);
    } else {
      result.callback();
    }
  };

  const setFreeTextWithLimit = (/** @type {string} */ val) => {
    if (val.length <= freeTextLimit)
      setFreeTextReason(val);
  };
  /** @template T */
  const setAndClearValidation = (/** @type {T} */ val, /** @type {(val: T) => void} */ set) => {
    setShowError(false);
    setShowSelectValidation(false);
    set(val);
  };

  const renderNextButton = (/** @type {React.ReactNode} */ label, /** @type {boolean} */ isLoading, /** @type {boolean | undefined} */ disabled, /** @type {(() => void) | undefined} */ callback) => {
    return isLoading ?
      <LoadingButton size='med' customClassNames={['account-closure__button']} /> :
      <Button size='med' customClassNames={['account-closure__button']} disabled={disabled} label={label} onClick={callback} />
  };

  return (
    <>
      <h2 className="modal__title">We're sorry you'd like to close your account.</h2>
      <p className="modal__slim">Before you do, do you have a moment to tell us why? Every bit of feedback helps us improve.</p>
      <p className="modal__slim modal__slim--extra-margin">
        <Select
          options={closureReasons}
          placeholder='Please select a reason'
          onChange={option => option && setAndClearValidation(option, setReason)}
        />
        <div className={showSelectValidation ? "login__error" : "login__error login__error--hide"}>
          <span className="login__error--wrap">Please select a reason.</span>
        </div>
      </p>      {reason && reason.value === "unhappy_with_service" &&
        <p className="modal__slim modal__slim--extra-margin">
          <TextAreaInput
            onChange={val => setAndClearValidation(val, setFreeTextWithLimit)}
            placeholder="Please tell us more about what you were unhappy with"
            value={freeTextReason}
          />
          <span className="account-closure__char-count">{freeTextReason.length}/{freeTextLimit} characters used</span>
        </p>
      }

      {reason && reason.value === "other" &&
        <p className="modal__slim modal__slim--extra-margin">
          <TextAreaInput
            onChange={val => setAndClearValidation(val, setFreeTextWithLimit)}
            placeholder="Please specify your reason for closing the account"
            value={freeTextReason}
          />
          <span className="account-closure__char-count">{freeTextReason.length}/{freeTextLimit} characters used</span>
        </p>
      }

    <div className={showError ? "login__error" : "login__error login__error--hide"}>
      <p className="modal__slim">
        <span className="login__error--wrap">Something went wrong, please try again.</span>
      </p>
    </div>

    <div className="modal__action-group">
      <ButtonGroup>
        {renderNextButton("Close account", isLoading, isLoading, proceed)}
        <Button variant='secondary' size='med' 
          //@ts-expect-error not a prop - perhaps customClassNames?
          className="account-closure__button" 
          label='Cancel' onClick={() => props.cancel('', '')} disabled={isLoading} />
      </ButtonGroup>
    </div>
    </>
  );
};

function StepFour(/** @type {StepFourProps} */ props) {
  return (
      <>
        <img src={successImg} className='account-activity__image' />
        <h4 className="modal__title-closure">Thank you</h4>
        <p className="modal__text-closure">Your account is scheduled to close.</p>
        <div className="modal__spacer" />
        <Button size='med' customClassNames={["modal__button"]} label="OK" onClick={props.close} />
      </>
  );
}

export default withFeatureFlags(AccountClosure);